import React, { useEffect, useState, useCallback } from 'react';
import { Card } from '../../components/ui/Card';
import { toast } from 'react-hot-toast';
import {
  Loader2, RefreshCw, Copy, Check, Code, Globe, Zap, Database,
  X, Eye, EyeOff, Info, Pencil, Trash2, Plus, KeyRound, Download, Apple
} from 'lucide-react';

// Platforms that support multi-account management via /api/accounts/:platform
const MULTI_ACCOUNT_PLATFORMS = ['GOOGLE_ADS', 'META_ADS', 'GOOGLE_ANALYTICS'];

// Brands allowed to use multi-account (whitelist)
const MULTI_ACCOUNT_BRAND_IDS = ['3ddb6c9f-19f6-4fa7-bf40-20f7f38ebaa6', '062be473-3b2b-4bad-a2e3-a31995e02d57', '44e35dfe-0186-495d-a0cc-db5e7e459ab7', '675cf508-b160-4767-b75e-43be853e3e59'];
// Markaya yalnız bazı platformlarda çoklu hesap (listede yoksa MULTI_ACCOUNT_PLATFORMS'un hepsi)
const MULTI_ACCOUNT_BRAND_PLATFORMS = {
  '675cf508-b160-4767-b75e-43be853e3e59': ['META_ADS'], // temizmama, 4 Kas 2026
};
const isMultiAccountFor = (brandId, platform) =>
  MULTI_ACCOUNT_PLATFORMS.includes(platform) && MULTI_ACCOUNT_BRAND_IDS.includes(brandId)
  && (!MULTI_ACCOUNT_BRAND_PLATFORMS[brandId] || MULTI_ACCOUNT_BRAND_PLATFORMS[brandId].includes(platform));

// OAuth connect URLs per platform
const OAUTH_URLS = {
  GOOGLE_ADS: 'https://api.glowytics.com/api/auth/google-ads',
  META_ADS: 'https://api.glowytics.com/api/auth/meta-ads',
  GOOGLE_ANALYTICS: 'https://api.glowytics.com/api/auth/google-analytics',
  TIKTOK_ADS: 'https://api.glowytics.com/api/auth/tiktok-ads',
};

// Platform catalog
const platforms = [
  {
    platform_key: 'GOOGLE_ANALYTICS',
    name: 'Google Analytics',
    icon: '/google-analytics.png',
    description: 'Import your website analytics data'
  },
  {
    platform_key: 'GOOGLE_ADS',
    name: 'Google Ads',
    icon: '/google-ads-icon.png',
    description: 'Connect your Google Ads account to track campaign performance'
  },
  {
    platform_key: 'META_ADS',
    name: 'Meta Ads',
    icon: '/meta-ads-icon.png',
    description: 'Connect Facebook and Instagram advertising accounts'
  },
  {
    platform_key: 'META_CATALOG',
    name: 'Meta Catalog',
    icon: '/meta-ads-icon.png',
    description: 'Sync product catalog for dynamic ads'
  },
  {
    platform_key: 'TIKTOK_ADS',
    name: 'TikTok Ads',
    icon: '/tiktok-icon.png',
    description: 'Track your TikTok advertising campaigns'
  },
  {
    platform_key: 'APPLE_SEARCH_ADS',
    name: 'Apple Search Ads',
    customIcon: 'apple',
    description: 'Connect Apple Search Ads to import campaign & keyword reports'
  },
  {
    platform_key: 'BIGQUERY',
    name: 'BigQuery',
    icon: null,
    customIcon: 'bigquery',
    description: 'Connect Google BigQuery to query and visualize your data warehouse'
  }
];

export default function ApiConnections() {
  const [isFetching, setIsFetching] = useState(false);
  const [connections, setConnections] = useState({});
  // accountsList: { GOOGLE_ADS: [...], META_ADS: [...], GOOGLE_ANALYTICS: [...] }
  const [accountsList, setAccountsList] = useState({});
  const [loadingAccounts, setLoadingAccounts] = useState({});
  const [bqModalOpen, setBqModalOpen] = useState(false);
  const [bqSaving, setBqSaving] = useState(false);
  const [bqForm, setBqForm] = useState({ projectId: '', refreshToken: '', accessToken: '', clientId: '', clientSecret: '' });
  const [bqShowTokens, setBqShowTokens] = useState(false);

  // Apple Search Ads connect modal
  const [appleModalOpen, setAppleModalOpen] = useState(false);
  const [appleSaving, setAppleSaving] = useState(false);
  const [appleGenerating, setAppleGenerating] = useState(false);
  const [appleShowKey, setAppleShowKey] = useState(false);
  const [applePublicKey, setApplePublicKey] = useState('');
  const [appleOrgOptions, setAppleOrgOptions] = useState(null);
  const [appleForm, setAppleForm] = useState({
    clientId: '', teamId: '', keyId: '', privateKey: '', orgId: '', nickname: '',
  });
  // Nickname edit state: { [connectionId]: string }
  const [editingNickname, setEditingNickname] = useState({});
  const [savingNickname, setSavingNickname] = useState({});
  const [deletingId, setDeletingId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const brandId = localStorage.getItem('currentBrandId') || '';

  const fetchConnections = useCallback(async () => {
    try {
      const res = await fetch('https://api.glowytics.com/api/auth/connections', {
        credentials: 'include',
        headers: { 'X-Brand-ID': brandId }
      });
      if (!res.ok) throw new Error('Failed to fetch connections');
      const data = await res.json();
      const map = {};
      data.forEach((c) => {
        const key = String(c.platform || '').toUpperCase();
        const prev = map[key];
        const candActive = String(c.status || '').toUpperCase() === 'ACTIVE';
        const candTs = c.createdAt ? new Date(c.createdAt).getTime() : 0;
        if (
          !prev ||
          (candActive && String(prev.status || '').toUpperCase() !== 'ACTIVE') ||
          candTs > (prev.createdAt ? new Date(prev.createdAt).getTime() : 0)
        ) {
          map[key] = c;
        }
      });
      const metaAdsKey = 'META_ADS';
      const metaCatalogKey = 'META_CATALOG';
      const statusOf = (o) => String(o?.status || '').toUpperCase();
      const isOk = (s) => ['ACTIVE', 'CONNECTED', 'AUTHORIZED', 'ENABLED'].includes(s);
      if (!isOk(statusOf(map[metaAdsKey])) && isOk(statusOf(map[metaCatalogKey]))) {
        map[metaAdsKey] = { ...map[metaCatalogKey], platform: metaAdsKey, status: 'CONNECTED' };
      }
      setConnections(map);
    } catch (e) {
      console.error('connections fetch error', e);
    }
  }, [brandId]);

  const fetchAccountsList = useCallback(async (platform) => {
    if (!brandId) return;
    setLoadingAccounts(prev => ({ ...prev, [platform]: true }));
    try {
      const res = await fetch(`https://api.glowytics.com/api/accounts/${platform}`, {
        credentials: 'include',
        headers: { 'X-Brand-ID': brandId }
      });
      if (!res.ok) throw new Error('Failed to fetch accounts');
      const data = await res.json();
      setAccountsList(prev => ({ ...prev, [platform]: data }));
    } catch (e) {
      console.error(`accounts list fetch error for ${platform}`, e);
    } finally {
      setLoadingAccounts(prev => ({ ...prev, [platform]: false }));
    }
  }, [brandId]);

  useEffect(() => {
    fetchConnections();
    MULTI_ACCOUNT_PLATFORMS.forEach(p => fetchAccountsList(p));
  }, [fetchConnections, fetchAccountsList]);

  // OAuth connect (adds new account without replacing existing ones)
  const handleOAuthConnect = async (platformKey) => {
    const url = OAUTH_URLS[platformKey];
    if (!url) return;
    if (!brandId) {
      toast.error('Please select a brand first');
      return;
    }
    try {
      const res = await fetch(url, {
        credentials: 'include',
        headers: { 'X-Brand-ID': brandId }
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No auth URL received');
      }
    } catch (e) {
      console.error('OAuth connect error', e);
      toast.error(e.message || 'Failed to start connection');
    }
  };

  // Nickname edit handlers
  const startEditNickname = (connectionId, currentNickname) => {
    setEditingNickname(prev => ({ ...prev, [connectionId]: currentNickname || '' }));
  };

  const cancelEditNickname = (connectionId) => {
    setEditingNickname(prev => {
      const next = { ...prev };
      delete next[connectionId];
      return next;
    });
  };

  const saveNickname = async (connectionId, platform) => {
    const nickname = editingNickname[connectionId] ?? '';
    setSavingNickname(prev => ({ ...prev, [connectionId]: true }));
    try {
      const res = await fetch(`https://api.glowytics.com/api/accounts/${connectionId}/nickname`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
        body: JSON.stringify({ nickname })
      });
      if (!res.ok) throw new Error('Failed to save nickname');
      toast.success('Nickname updated');
      cancelEditNickname(connectionId);
      fetchAccountsList(platform);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSavingNickname(prev => ({ ...prev, [connectionId]: false }));
    }
  };

  // Remove account
  const handleRemoveAccount = async (connectionId, platform) => {
    setDeletingId(connectionId);
    try {
      const res = await fetch(`https://api.glowytics.com/api/accounts/${connectionId}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'X-Brand-ID': brandId }
      });
      if (!res.ok) throw new Error('Failed to remove account');
      toast.success('Account removed');
      setConfirmDeleteId(null);
      fetchAccountsList(platform);
      fetchConnections();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setDeletingId(null);
    }
  };

  const handleBqConnect = async () => {
    if (!bqForm.refreshToken && !bqForm.accessToken) {
      toast.error('Refresh Token veya Access Token gerekli');
      return;
    }
    setBqSaving(true);
    try {
      const res = await fetch('https://api.glowytics.com/api/auth/bigquery/connect', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
        body: JSON.stringify({
          projectId: bqForm.projectId || undefined,
          refreshToken: bqForm.refreshToken || undefined,
          accessToken: bqForm.accessToken || undefined,
          clientId: bqForm.clientId || undefined,
          clientSecret: bqForm.clientSecret || undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Connection failed');
      toast.success('BigQuery bağlantısı kaydedildi');
      setBqModalOpen(false);
      setBqForm({ projectId: '', refreshToken: '', accessToken: '', clientId: '', clientSecret: '' });
      fetchConnections();
    } catch (e) {
      toast.error(e.message);
    } finally { setBqSaving(false); }
  };

  const handleTikTokDisconnect = async () => {
    if (!window.confirm('TikTok Ads bağlantısını kaldırmak istediğinize emin misiniz?')) return;
    try {
      const res = await fetch('https://api.glowytics.com/api/auth/tiktok-ads/disconnect', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
      });
      if (!res.ok) throw new Error('Disconnect failed');
      toast.success('TikTok Ads bağlantısı kaldırıldı');
      fetchConnections();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const handleBqDisconnect = async () => {
    if (!window.confirm('BigQuery bağlantısını kaldırmak istediğinize emin misiniz?')) return;
    try {
      const res = await fetch('https://api.glowytics.com/api/auth/bigquery/disconnect', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
      });
      if (!res.ok) throw new Error('Disconnect failed');
      toast.success('BigQuery bağlantısı kaldırıldı');
      fetchConnections();
    } catch (e) {
      toast.error(e.message);
    }
  };

  // ── Apple Search Ads ──────────────────────────────────────────────────────
  const generateAppleKeys = async () => {
    setAppleGenerating(true);
    try {
      if (!window.crypto?.subtle) {
        throw new Error('Tarayıcı WebCrypto desteklemiyor (HTTPS gerekli).');
      }
      const keyPair = await window.crypto.subtle.generateKey(
        { name: 'ECDSA', namedCurve: 'P-256' }, true, ['sign', 'verify'],
      );
      const [privBuf, pubBuf] = await Promise.all([
        window.crypto.subtle.exportKey('pkcs8', keyPair.privateKey),
        window.crypto.subtle.exportKey('spki', keyPair.publicKey),
      ]);
      const toPem = (buf, label) => {
        const bytes = new Uint8Array(buf);
        let str = '';
        for (let i = 0; i < bytes.byteLength; i++) str += String.fromCharCode(bytes[i]);
        const b64 = window.btoa(str).match(/.{1,64}/g).join('\n');
        return `-----BEGIN ${label}-----\n${b64}\n-----END ${label}-----\n`;
      };
      setAppleForm(p => ({ ...p, privateKey: toPem(privBuf, 'PRIVATE KEY') }));
      setApplePublicKey(toPem(pubBuf, 'PUBLIC KEY'));
      toast.success('Anahtar çifti üretildi. Public key’i Apple Ads’e yükleyin.');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setAppleGenerating(false);
    }
  };

  const downloadApplePrivateKey = () => {
    if (!appleForm.privateKey) return;
    const blob = new Blob([appleForm.privateKey], { type: 'application/x-pem-file' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'apple-search-ads-private-key.pem';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleAppleConnect = async () => {
    if (!brandId) { toast.error('Önce bir marka seçin'); return; }
    const { clientId, keyId, privateKey } = appleForm;
    if (!clientId.trim() || !keyId.trim() || !privateKey.trim()) {
      toast.error('Client ID, Key ID ve Private Key zorunlu');
      return;
    }
    setAppleSaving(true);
    setAppleOrgOptions(null);
    try {
      const res = await fetch('https://api.glowytics.com/api/apple-search-ads/connect', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
        body: JSON.stringify({
          clientId: clientId.trim(),
          teamId: appleForm.teamId.trim() || null,
          keyId: keyId.trim(),
          privateKey,
          orgId: appleForm.orgId.trim() || null,
          nickname: appleForm.nickname.trim() || null,
        }),
      });
      const data = await res.json();
      if (res.status === 409 && data.options) {
        setAppleOrgOptions(data.options);
        toast.error('Birden fazla organizasyon bulundu — birini seçin.');
        return;
      }
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      toast.success('Apple Search Ads bağlandı');
      setAppleModalOpen(false);
      setAppleForm({ clientId: '', teamId: '', keyId: '', privateKey: '', orgId: '', nickname: '' });
      setApplePublicKey('');
      fetchConnections();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setAppleSaving(false);
    }
  };

  const handleAppleDisconnect = async () => {
    if (!window.confirm('Apple Search Ads bağlantısını kaldırmak istediğinize emin misiniz?')) return;
    try {
      const res = await fetch('https://api.glowytics.com/api/apple-search-ads/disconnect', {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'X-Brand-ID': brandId },
      });
      if (!res.ok) throw new Error('Disconnect failed');
      toast.success('Apple Search Ads bağlantısı kaldırıldı');
      fetchConnections();
    } catch (e) {
      toast.error(e.message);
    }
  };

  // Render a single connected account card
  const renderAccountCard = (account, platform) => {
    const displayName = account.nickname || account.accountName || account.customerId || 'Account';
    const editing = account.id in editingNickname;

    return (
      <div key={account.id} className="flex items-center gap-3 p-3 rounded-lg border border-gray-100 bg-gray-50 hover:bg-white hover:shadow-sm transition-all">
        {/* Account info */}
        <div className="flex-1 min-w-0">
          {editing ? (
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={editingNickname[account.id]}
                onChange={e => setEditingNickname(prev => ({ ...prev, [account.id]: e.target.value }))}
                onKeyDown={e => {
                  if (e.key === 'Enter') saveNickname(account.id, platform);
                  if (e.key === 'Escape') cancelEditNickname(account.id);
                }}
                autoFocus
                className="flex-1 min-w-0 px-2 py-1 text-sm border border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500/40"
                placeholder="Account nickname"
              />
              <button
                onClick={() => saveNickname(account.id, platform)}
                disabled={savingNickname[account.id]}
                className="px-2 py-1 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 rounded-md flex items-center gap-1"
              >
                {savingNickname[account.id] ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                Save
              </button>
              <button
                onClick={() => cancelEditNickname(account.id)}
                className="px-2 py-1 text-xs font-medium text-gray-600 hover:text-gray-800 rounded-md"
              >
                Cancel
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-sm font-medium text-gray-800 truncate">{displayName}</span>
            </div>
          )}
          {!editing && account.customerId && (
            <p className="text-xs text-gray-400 mt-0.5">ID: {account.customerId}</p>
          )}
        </div>

        {/* Actions */}
        {!editing && (
          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              onClick={() => startEditNickname(account.id, account.nickname)}
              title="Edit nickname"
              className="p-1.5 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-200 transition-colors"
            >
              <Pencil className="w-3.5 h-3.5" />
            </button>
            {confirmDeleteId === account.id ? (
              <div className="flex items-center gap-1">
                <span className="text-xs text-red-600">Remove?</span>
                <button
                  onClick={() => handleRemoveAccount(account.id, platform)}
                  disabled={deletingId === account.id}
                  className="px-2 py-1 text-xs font-medium text-white bg-red-600 hover:bg-red-700 disabled:bg-gray-300 rounded-md flex items-center gap-1"
                >
                  {deletingId === account.id ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Yes'}
                </button>
                <button
                  onClick={() => setConfirmDeleteId(null)}
                  className="px-2 py-1 text-xs font-medium text-gray-600 hover:text-gray-800 rounded-md"
                >
                  No
                </button>
              </div>
            ) : (
              <button
                onClick={() => setConfirmDeleteId(account.id)}
                title="Remove account"
                className="p-1.5 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderPlatform = (platform) => {
    const key = String(platform.platform_key || '').toUpperCase();
    const isMultiAccount = isMultiAccountFor(brandId, key);
    const allowed = new Set(['ACTIVE', 'CONNECTED', 'AUTHORIZED', 'ENABLED']);
    const statusOf = (o) => String(o?.status || '').toUpperCase();
    const ok = (o) => !!o && (allowed.has(statusOf(o)) || !!o.accessToken || !!o.customerId || !!o.accountName);

    const k = (k) => connections[String(k).toUpperCase()];
    let isConnected;
    let displayId;

    if (key === 'META_ADS') {
      const meta = k('META_ADS') || k('FACEBOOK_ADS') || k('FB_ADS');
      const catalog = k('META_CATALOG');
      isConnected = !!meta || ok(meta) || ok(catalog);
      displayId = meta?.customerId || meta?.accountName || catalog?.customerId || catalog?.accountName;
    } else {
      const conn = k(key);
      isConnected = ok(conn);
      displayId = conn?.customerId || conn?.accountName;
    }

    const accounts = accountsList[key] || [];
    const isLoading = loadingAccounts[key];

    const onConnectLegacy =
      key === 'BIGQUERY'
        ? () => setBqModalOpen(true)
        : key === 'APPLE_SEARCH_ADS'
        ? () => setAppleModalOpen(true)
        : OAUTH_URLS[key]
        ? () => handleOAuthConnect(key)
        : undefined;
    const onDisconnect =
      key === 'BIGQUERY'
        ? handleBqDisconnect
        : key === 'APPLE_SEARCH_ADS'
        ? handleAppleDisconnect
        : key === 'TIKTOK_ADS'
        ? handleTikTokDisconnect
        : undefined;

    return (
      <Card key={platform.name} className="p-6 rounded-xl">
        <div className="flex items-start justify-between gap-4">
          {/* Platform icon + info */}
          <div className="flex items-center gap-4 flex-1 min-w-0">
            <div className="w-12 h-12 flex-shrink-0 flex items-center justify-center">
              {platform.customIcon === 'bigquery' ? (
                <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                  <Database className="w-5 h-5 text-blue-600" />
                </div>
              ) : platform.customIcon === 'apple' ? (
                <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
                  <Apple className="w-5 h-5 text-gray-800" />
                </div>
              ) : (
                <img src={platform.icon} alt={platform.name} className="w-full h-full object-contain" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-medium text-gray-900">{platform.name}</h3>
              <p className="text-sm text-gray-500">{platform.description}</p>
              {!isMultiAccount && displayId && (
                <p className="text-xs text-gray-500 mt-1">ID: {displayId}</p>
              )}
            </div>
          </div>

          {/* Actions (right side) */}
          <div className="flex items-center gap-3 flex-shrink-0">
            {isMultiAccount ? (
              <button
                onClick={() => handleOAuthConnect(key)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 transition-colors"
              >
                <Plus className="w-3.5 h-3.5" />
                Add New Account
              </button>
            ) : isConnected ? (
              <>
                <button
                  onClick={onDisconnect}
                  className="px-3 py-1 rounded-full text-sm font-medium text-red-700 bg-red-50 hover:bg-red-100"
                >
                  Disconnect
                </button>
                <span className="px-3 py-1 rounded-full text-sm font-medium text-green-700 bg-green-50">
                  Connected
                </span>
              </>
            ) : (
              <button
                onClick={onConnectLegacy}
                className="px-3 py-1 rounded-full text-sm font-medium text-blue-700 bg-blue-50 hover:bg-blue-100"
              >
                Connect
              </button>
            )}
          </div>
        </div>

        {/* Multi-account list */}
        {isMultiAccount && (
          <div className="mt-4">
            {isLoading ? (
              <div className="flex items-center gap-2 text-sm text-gray-500 py-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading accounts...
              </div>
            ) : accounts.length === 0 ? (
              <p className="text-sm text-gray-400 italic">
                No connected accounts. Click "Add New Account" to connect.
              </p>
            ) : (
              <div className="space-y-2">
                {accounts.map(account => renderAccountCard(account, key))}
              </div>
            )}
          </div>
        )}
      </Card>
    );
  };

  const handleFetchLatestData = async () => {
    setIsFetching(true);
    try {
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) {
        throw new Error('Please select a brand first');
      }

      const response = await fetch(`https://api.glowytics.com/api/brands/${brandId}/fetch-data`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': brandId
        },
        body: JSON.stringify({
          mode: 'full'
        }),
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to fetch latest data');
      }

      toast.success('Full mode data fetch started successfully');
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to fetch latest data: ' + error.message);
    } finally {
      setIsFetching(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">API Connections</h2>
          <p className="text-sm text-gray-500 mt-1">Manage your platform integrations and data sources</p>
        </div>

        <div className="flex gap-2">
          <button
            onClick={handleFetchLatestData}
            disabled={isFetching}
            className={`
              px-4 py-2 rounded-lg text-white
              ${isFetching ? 'bg-gray-400' : 'bg-blue-600 hover:bg-blue-700'}
              flex items-center gap-2
            `}
          >
            {isFetching ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Fetching Data...</span>
              </>
            ) : (
              <>
                <RefreshCw className="w-4 h-4" />
                <span>Fetch Latest Data</span>
              </>
            )}
          </button>
        </div>
      </div>

      <div className="space-y-4">
        {platforms.map(renderPlatform)}
      </div>

      {/* Apple Search Ads Connect Modal */}
      {appleModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gray-100 rounded-lg">
                  <Apple className="w-5 h-5 text-gray-800" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Apple Search Ads Bağlantısı</h3>
              </div>
              <button onClick={() => setAppleModalOpen(false)} className="p-1 hover:bg-gray-100 rounded-lg transition-colors">
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            <div className="px-6 py-5 space-y-4">
              {/* Step guide */}
              <div className="flex items-start gap-2 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2.5">
                <Info className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                <div className="text-xs text-blue-700 leading-relaxed">
                  <p className="font-medium mb-1">Apple Search Ads kimlik bilgileri nasıl alınır?</p>
                  <ol className="list-decimal ml-4 space-y-0.5 text-blue-600">
                    <li>Apple Ads → <b>Account Settings → User Management</b>’dan <b>API</b> rolünde kullanıcı davet edin.</li>
                    <li>Aşağıdaki <b>“Anahtar Üret”</b> ile P-256 anahtar çifti oluşturun.</li>
                    <li><b>Public key</b>’i kopyalayıp Apple Ads → <b>Account Settings → API → Public Key</b> alanına yapıştırıp <b>Save</b>’e basın.</li>
                    <li>Apple’ın gösterdiği <b>Client ID, Team ID, Key ID</b>’yi aşağıya yapıştırın.</li>
                    <li>(Opsiyonel) <b>Org ID</b> boş bırakılırsa otomatik bulunur; birden fazla org varsa seçtirilir.</li>
                  </ol>
                </div>
              </div>

              {/* Key generation */}
              <div className="border border-gray-200 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-gray-700">1) Anahtar çifti</label>
                  <button onClick={generateAppleKeys} disabled={appleGenerating}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-white bg-gray-800 hover:bg-gray-900 disabled:bg-gray-300">
                    {appleGenerating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <KeyRound className="w-3.5 h-3.5" />}
                    Anahtar Üret
                  </button>
                </div>
                {applePublicKey ? (
                  <div className="space-y-2">
                    <p className="text-xs text-gray-500">Bu <b>Public Key</b>’i kopyalayıp Apple Ads’e yükleyin:</p>
                    <textarea readOnly value={applePublicKey} rows={4}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-[11px] font-mono text-gray-700 bg-gray-50 resize-none" />
                    <div className="flex items-center gap-2">
                      <button onClick={() => { navigator.clipboard.writeText(applePublicKey); toast.success('Public key kopyalandı'); }}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100">
                        <Copy className="w-3.5 h-3.5" /> Public Key Kopyala
                      </button>
                      <button onClick={downloadApplePrivateKey}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200">
                        <Download className="w-3.5 h-3.5" /> Private Key’i indir
                      </button>
                    </div>
                    <p className="text-[11px] text-amber-600">Private key tarayıcıda üretildi ve forma yerleştirildi. Bir kopyasını güvenli saklayın.</p>
                  </div>
                ) : (
                  <p className="text-xs text-gray-400">Henüz anahtar üretilmedi. Kendi anahtarınız varsa Private Key’i aşağıya yapıştırabilirsiniz.</p>
                )}
              </div>

              {/* Credential fields */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Client ID</label>
                  <input type="text" value={appleForm.clientId} onChange={e => setAppleForm(p => ({ ...p, clientId: e.target.value }))}
                    placeholder="SEARCHADS.aeb3ef5f-..."
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Team ID</label>
                  <input type="text" value={appleForm.teamId} onChange={e => setAppleForm(p => ({ ...p, teamId: e.target.value }))}
                    placeholder="SEARCHADS.hgw3ef3p-..."
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Key ID</label>
                  <input type="text" value={appleForm.keyId} onChange={e => setAppleForm(p => ({ ...p, keyId: e.target.value }))}
                    placeholder="a273d0d3-4d9e-..."
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Org ID <span className="text-gray-400 font-normal">(opsiyonel)</span></label>
                  <input type="text" value={appleForm.orgId} onChange={e => setAppleForm(p => ({ ...p, orgId: e.target.value }))}
                    placeholder="40669820"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nickname <span className="text-gray-400 font-normal">(opsiyonel)</span></label>
                <input type="text" value={appleForm.nickname} onChange={e => setAppleForm(p => ({ ...p, nickname: e.target.value }))}
                  placeholder="Apple Ads - Ana Hesap"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500" />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-gray-700">Private Key (PEM)</label>
                  <button onClick={() => setAppleShowKey(!appleShowKey)} className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1">
                    {appleShowKey ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                    {appleShowKey ? 'Gizle' : 'Göster'}
                  </button>
                </div>
                {appleShowKey ? (
                  <textarea value={appleForm.privateKey} onChange={e => setAppleForm(p => ({ ...p, privateKey: e.target.value }))} rows={5}
                    placeholder={'-----BEGIN PRIVATE KEY-----\n...'}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-[11px] font-mono text-gray-800 placeholder-gray-400 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500" />
                ) : (
                  <div className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-400 bg-gray-50">
                    {appleForm.privateKey ? '•••••• (private key girildi)' : 'Private key girilmedi'}
                  </div>
                )}
              </div>

              {/* Org selection (multi-org) */}
              {appleOrgOptions && appleOrgOptions.length > 0 && (
                <div className="border border-amber-200 bg-amber-50 rounded-lg p-3">
                  <p className="text-xs font-medium text-amber-800 mb-2">Birden fazla organizasyon bulundu — birini seçin:</p>
                  <div className="space-y-1.5">
                    {appleOrgOptions.map(o => (
                      <button key={o.orgId} onClick={() => setAppleForm(p => ({ ...p, orgId: String(o.orgId) }))}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm border transition-colors ${String(appleForm.orgId) === String(o.orgId) ? 'border-blue-500 bg-blue-50 text-blue-800' : 'border-gray-200 bg-white hover:bg-gray-50 text-gray-700'}`}>
                        <span className="font-medium">{o.orgName || 'Org'}</span>
                        <span className="text-gray-400"> — {o.orgId}{o.currency ? ` (${o.currency})` : ''}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50 sticky bottom-0">
              <button onClick={() => setAppleModalOpen(false)}
                className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 transition-colors">
                İptal
              </button>
              <button onClick={handleAppleConnect} disabled={appleSaving || !appleForm.clientId || !appleForm.keyId || !appleForm.privateKey}
                className="px-5 py-2 bg-gray-800 hover:bg-gray-900 disabled:bg-gray-300 disabled:text-gray-500 text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-2">
                {appleSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Apple className="w-4 h-4" />}
                Bağlan
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Glowytics Pixel Section */}
      <PixelCodeSection />

      {/* BigQuery Connect Modal */}
      {bqModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-50 rounded-lg">
                  <Database className="w-5 h-5 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">BigQuery Bağlantısı</h3>
              </div>
              <button onClick={() => setBqModalOpen(false)} className="p-1 hover:bg-gray-100 rounded-lg transition-colors">
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            <div className="px-6 py-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Project ID</label>
                <input type="text" value={bqForm.projectId} onChange={e => setBqForm(p => ({ ...p, projectId: e.target.value }))}
                  placeholder="skechers-ga4"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500" />
                <p className="text-xs text-gray-400 mt-1">Opsiyonel — BigQuery Explorer'da da girebilirsiniz</p>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-gray-700">Refresh Token</label>
                  <button onClick={() => setBqShowTokens(!bqShowTokens)} className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1">
                    {bqShowTokens ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                    {bqShowTokens ? 'Gizle' : 'Göster'}
                  </button>
                </div>
                <input type={bqShowTokens ? 'text' : 'password'} value={bqForm.refreshToken}
                  onChange={e => setBqForm(p => ({ ...p, refreshToken: e.target.value }))}
                  placeholder="1//0abc..."
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Access Token <span className="text-gray-400 font-normal">(opsiyonel)</span></label>
                <input type={bqShowTokens ? 'text' : 'password'} value={bqForm.accessToken}
                  onChange={e => setBqForm(p => ({ ...p, accessToken: e.target.value }))}
                  placeholder="ya29.a0..."
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500" />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Client ID <span className="text-gray-400 font-normal">(opsiyonel)</span></label>
                  <input type={bqShowTokens ? 'text' : 'password'} value={bqForm.clientId}
                    onChange={e => setBqForm(p => ({ ...p, clientId: e.target.value }))}
                    placeholder="123...apps.googleusercontent.com"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Client Secret <span className="text-gray-400 font-normal">(opsiyonel)</span></label>
                  <input type={bqShowTokens ? 'text' : 'password'} value={bqForm.clientSecret}
                    onChange={e => setBqForm(p => ({ ...p, clientSecret: e.target.value }))}
                    placeholder="GOCSPX-..."
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500" />
                </div>
              </div>
              <p className="text-xs text-gray-400 -mt-2">Token'ı Playground'dan aldıysanız Playground'un client bilgilerini girin. Boş bırakırsanız sistem varsayılanları kullanılır.</p>

              <div className="flex items-start gap-2 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2.5">
                <Info className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                <div className="text-xs text-blue-700 leading-relaxed">
                  <p className="font-medium mb-1">Token nasıl alınır?</p>
                  <ol className="list-decimal ml-4 space-y-0.5 text-blue-600">
                    <li><a href="https://developers.google.com/oauthplayground/" target="_blank" rel="noopener noreferrer" className="underline">OAuth Playground</a>'a gidin</li>
                    <li>"BigQuery API v2" &rarr; <code className="bg-blue-100 px-1 rounded">bigquery</code> scope seçin</li>
                    <li>"Authorize APIs" &rarr; Google hesabınızla giriş yapın</li>
                    <li>"Exchange authorization code for tokens" &rarr; Refresh Token'ı kopyalayın</li>
                    <li>Client ID / Secret: Playground varsayılanlarını veya kendi GCP projenizinkini girin</li>
                  </ol>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50">
              <button onClick={() => setBqModalOpen(false)}
                className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 transition-colors">
                İptal
              </button>
              <button onClick={handleBqConnect} disabled={bqSaving || (!bqForm.refreshToken && !bqForm.accessToken)}
                className="px-5 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:text-gray-500 text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-2">
                {bqSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Database className="w-4 h-4" />}
                Kaydet
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// Glowytics Pixel — Embed Code Generator
// ─────────────────────────────────────────────

function PixelCodeSection() {
  const brandId = localStorage.getItem('currentBrandId') || '';
  const brandName = localStorage.getItem('currentBrandName') || 'Your Brand';
  const [copied, setCopied] = useState(null); // 'script' | 'gtm' | null

  const scriptCode = `<!-- Glowytics Pixel — ${brandName} -->
<script>
  window.glowyticsConfig = { brandId: '${brandId}' };
</script>
<script src="https://api.glowytics.com/pixel/glowytics-pixel.js" async></script>`;

  const gtmCode = `<script>
  window.glowyticsConfig = { brandId: '${brandId}' };
  (function() {
    var s = document.createElement('script');
    s.src = 'https://api.glowytics.com/pixel/glowytics-pixel.js';
    s.async = true;
    document.head.appendChild(s);
  })();
</script>`;

  const handleCopy = useCallback((type) => {
    const text = type === 'gtm' ? gtmCode : scriptCode;
    navigator.clipboard.writeText(text).then(() => {
      setCopied(type);
      toast.success('Pixel code copied to clipboard!');
      setTimeout(() => setCopied(null), 3000);
    }).catch(() => {
      toast.error('Failed to copy');
    });
  }, [scriptCode, gtmCode]);

  if (!brandId) return null;

  return (
    <div className="mt-10 pt-8 border-t border-gray-200">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2.5 rounded-xl bg-gradient-to-br from-indigo-50 to-purple-50">
          <Code className="w-5 h-5 text-indigo-600" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Glowytics Pixel</h2>
          <p className="text-sm text-gray-500">Track visitors, events, and conversions on your website</p>
        </div>
      </div>

      {/* What it tracks */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
        <div className="flex items-center gap-2.5 p-3 rounded-lg bg-gray-50 border border-gray-100">
          <Globe className="w-4 h-4 text-blue-500 flex-shrink-0" />
          <div>
            <p className="text-xs font-medium text-gray-700">Page Views & Sessions</p>
            <p className="text-[10px] text-gray-500">UTM parameters, referrer, session tracking</p>
          </div>
        </div>
        <div className="flex items-center gap-2.5 p-3 rounded-lg bg-gray-50 border border-gray-100">
          <Zap className="w-4 h-4 text-amber-500 flex-shrink-0" />
          <div>
            <p className="text-xs font-medium text-gray-700">GA4 Ecommerce Events</p>
            <p className="text-[10px] text-gray-500">Auto-captures dataLayer events (purchase, add_to_cart...)</p>
          </div>
        </div>
        <div className="flex items-center gap-2.5 p-3 rounded-lg bg-gray-50 border border-gray-100">
          <Code className="w-4 h-4 text-green-500 flex-shrink-0" />
          <div>
            <p className="text-xs font-medium text-gray-700">Custom Events</p>
            <p className="text-[10px] text-gray-500">glowytics.track('event_name', data)</p>
          </div>
        </div>
      </div>

      {/* Direct Script Tag */}
      <Card className="rounded-xl overflow-hidden mb-4">
        <div className="flex items-center justify-between p-4 bg-gray-50 border-b border-gray-100">
          <div>
            <h3 className="text-sm font-semibold text-gray-800">Option 1: Direct Script Tag</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Paste this code before the closing <code className="bg-gray-200 px-1 rounded text-[10px]">&lt;/head&gt;</code> tag on every page
            </p>
          </div>
          <button
            onClick={() => handleCopy('script')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              copied === 'script'
                ? 'bg-green-100 text-green-700 border border-green-200'
                : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
            }`}
          >
            {copied === 'script' ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
            {copied === 'script' ? 'Copied!' : 'Copy'}
          </button>
        </div>
        <pre className="p-4 bg-gray-900 text-gray-100 text-xs leading-relaxed overflow-x-auto">
          <code>{scriptCode}</code>
        </pre>
      </Card>

      {/* GTM Custom HTML Tag */}
      <Card className="rounded-xl overflow-hidden mb-4">
        <div className="flex items-center justify-between p-4 bg-gray-50 border-b border-gray-100">
          <div>
            <h3 className="text-sm font-semibold text-gray-800">Option 2: Google Tag Manager (GTM)</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Create a <strong>Custom HTML</strong> tag in GTM, paste this code, trigger on <strong>All Pages</strong>
            </p>
          </div>
          <button
            onClick={() => handleCopy('gtm')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              copied === 'gtm'
                ? 'bg-green-100 text-green-700 border border-green-200'
                : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
            }`}
          >
            {copied === 'gtm' ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
            {copied === 'gtm' ? 'Copied!' : 'Copy'}
          </button>
        </div>
        <pre className="p-4 bg-gray-900 text-gray-100 text-xs leading-relaxed overflow-x-auto">
          <code>{gtmCode}</code>
        </pre>
      </Card>

      {/* Brand ID Info */}
      <div className="flex items-center gap-2 p-3 rounded-lg bg-indigo-50 border border-indigo-100">
        <div className="flex-shrink-0">
          <svg className="w-4 h-4 text-indigo-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
        </div>
        <div>
          <p className="text-xs text-indigo-800">
            <strong>Brand ID:</strong> <code className="bg-indigo-100 px-1.5 py-0.5 rounded text-[10px] select-all">{brandId}</code>
            <span className="ml-2 text-indigo-600">This pixel is unique to <strong>{brandName}</strong>. Each brand has its own pixel code and isolated data.</span>
          </p>
        </div>
      </div>

      {/* What gets captured */}
      <details className="mt-4">
        <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-700">
          What does the pixel capture? (click to expand)
        </summary>
        <div className="mt-2 p-4 bg-gray-50 rounded-lg text-xs text-gray-600 space-y-1.5">
          <p><strong>Automatic:</strong> Page views, sessions (30 min timeout), UTM parameters (source, medium, campaign), visitor ID (first-party cookie), referrer, screen size</p>
          <p><strong>GA4 dataLayer (auto-detected):</strong> view_item, add_to_cart, remove_from_cart, begin_checkout, purchase (with transaction_id, value, items), view_item_list, search</p>
          <p><strong>Manual tracking:</strong> <code className="bg-gray-200 px-1 rounded">glowytics.track('custom_event', {'{'} key: 'value' {'}'})</code></p>
          <p><strong>Session rules:</strong> 30 min inactivity timeout, midnight boundary, UTM change = new session, max 4 hours</p>
          <p><strong>Privacy:</strong> No PII collected. First-party cookie only. GDPR/KVKK compatible.</p>
        </div>
      </details>
    </div>
  );
}
